import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import Link from '@atoms/Link/Link';
import Icon from '@atoms/Icon/Icon';
import { StyledCloseIcon } from '@atoms/Tile/Tile.styles';
import Text from '@atoms/Text/Text';
import { BreadcrumbDataSearchStateData, FacetDataObject } from '@occ/api-client';
import { BreadcrumbDataWithFaceValueName } from '@molecules/SelectedFilters/SelectedFilters';
import PlusSmall from '@icons/plus_small.svg';
import MinusSmall from '@icons/minus_small.svg';
import CloseSmall from '@icons/close_small.svg';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  StyledFilterCheckBoxAndCount,
  StyledFilterList,
  StyledFilterListCheckbox,
  StyledFilterListLabel,
  StyledFilterListOptions,
  StyledFilterListSelectedText,
  StyledFilterListSelectedTextContainer,
  StyledFilterListSpan,
} from './FilterList.styles';

type Props = {
  code: string;
  name: string;
  option: FacetDataObject;
  expandedListName: string;
  toggleList: () => void;
  breadcrumbs: Array<BreadcrumbDataSearchStateData>;
};

const FilterList = ({
  option = {} as FacetDataObject,
  name,
  expandedListName,
  toggleList,
  breadcrumbs,
  code,
}: Props) => {
  const filterListOptions = useRef<HTMLDivElement>(null);
  const expandedFilterCategoryRef = useRef<HTMLButtonElement>(null);
  const t = useTranslations('productFilter');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentQuery = searchParams ? Object.fromEntries(searchParams.entries()) : {};

  useEffect(() => {
    setTimeout(() => {
      if (expandedFilterCategoryRef?.current) expandedFilterCategoryRef.current.focus();
    }, 0);
  }, []);

  return (
    <>
      {option && (
        <>
          <StyledFilterList
            ref={expandedListName === option.name ? expandedFilterCategoryRef : undefined}
            variant="transparent"
            $expanded={expandedListName === option.name}
            ariaExpanded={expandedListName === option.name}
            onClick={toggleList}
            key={option.name}
          >
            <div>
              <StyledFilterListSpan>{name}</StyledFilterListSpan>
              {expandedListName !== option.name && breadcrumbs.length > 0 && (
                <StyledFilterListSelectedTextContainer>
                  {breadcrumbs
                    .filter((breadcrumb) => breadcrumb.facetCode === code)
                    .filter(
                      (breadcrumb): breadcrumb is BreadcrumbDataWithFaceValueName =>
                        breadcrumb.facetValueName !== undefined
                    )
                    .map((breadcrumb, index) => (
                      <StyledFilterListSelectedText
                        type="body"
                        size="small"
                        key={`${breadcrumb.facetValueName}-${index}`}
                      >
                        {`${index > 0 ? ', ' : ''}${breadcrumb.facetValueName}`}
                      </StyledFilterListSelectedText>
                    ))}
                </StyledFilterListSelectedTextContainer>
              )}
            </div>
            {expandedListName === option.name ? (
              <Icon svg={MinusSmall} size={12} color="primary" />
            ) : (
              <Icon svg={PlusSmall} size={12} />
            )}
          </StyledFilterList>
          <StyledFilterListOptions $expanded={expandedListName === option.name} ref={filterListOptions}>
            <div>
              {option?.values?.map((value: any) => {
                let filterQuery = value?.query?.query?.value;
                if (pathname?.startsWith('/erbjudanden')) {
                  filterQuery = decodeURIComponent(value?.query?.url);
                }
                if (value.name && value.code && (filterQuery || (!filterQuery && value?.selected))) {
                  // Build query params with new filter
                  const newQuery = { ...currentQuery, q: filterQuery };
                  const queryString = new URLSearchParams(newQuery as any).toString();
                  const newPath = queryString ? `${pathname}?${queryString}` : pathname || '';

                  return (
                    <Link
                      scroll={false}
                      href={newPath}
                      key={value.code}
                      shallow
                      aria-label={t('accessibility.filterOption', { name: value.name, count: value.count })}
                    >
                      <StyledFilterListLabel>
                        <Text type="body" size="medium">
                          {value.name}
                        </Text>

                        <StyledFilterCheckBoxAndCount>
                          {value.count && (
                            <Text type="body" size="medium">
                              {value.count}
                            </Text>
                          )}
                          {!value.count && <StyledCloseIcon svg={CloseSmall} size={9} />}
                          <StyledFilterListCheckbox
                            checked={value.selected}
                            onChange={() => !value.selected}
                            id={value.name}
                          />
                        </StyledFilterCheckBoxAndCount>
                      </StyledFilterListLabel>
                    </Link>
                  );
                }
                return false;
              })}
            </div>
          </StyledFilterListOptions>
        </>
      )}
    </>
  );
};

export default FilterList;
